/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  Loading,
  RouteDefinition,
  useNavigationContext,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useEffect } from "preact/hooks";
import { usePreferences } from "../hooks/preferences.js";
import { useBankState } from "../hooks/bank-state.js";
import { useSessionState } from "../hooks/session.js";
import { OperationState } from "./OperationState/index.js";

export function CashAcceptor({
  routeCancel,
  routeOperation,
}: {
  routeCancel: RouteDefinition;
  routeOperation: RouteDefinition<{ wopid: string }>;
}): VNode {
  const { i18n } = useTranslationContext();
  const { navigateTo } = useNavigationContext();
  const [preferences] = usePreferences();
  const [bankState] = useBankState();
  const { state: session } = useSessionState();
  const { url: backendUrl } = useBankCoreApiContext();
  const active =
    session.status === "loggedIn" &&
    bankState.activeWithdrawal?.username === session.username &&
    bankState.activeWithdrawal.backendBaseUrl === backendUrl.href
      ? bankState.activeWithdrawal
      : undefined;

  useEffect(() => {
    if (!preferences.cashAcceptorSimulator) {
      navigateTo(routeCancel.url({}));
    } else if (active) {
      navigateTo(routeOperation.url({ wopid: active.operationId }));
    }
  }, [
    active,
    navigateTo,
    preferences.cashAcceptorSimulator,
    routeCancel,
    routeOperation,
  ]);

  if (!preferences.cashAcceptorSimulator || active) return <Loading />;

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-primaryDark hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to overview</i18n.Translate>
      </a>
      <OperationState
        creationMode="cash-acceptor"
        routeClose={routeCancel}
        onAbort={() => navigateTo(routeCancel.url({}))}
        onOperationCreated={(wopid) =>
          navigateTo(routeOperation.url({ wopid }))
        }
      />
    </section>
  );
}
